/**
 * UI Enhancements for CAD System
 * Tooltips, property panels, context menus, and other UX improvements
 */

class CADUIEnhancements {
  constructor(cadViewer) {
    this.cadViewer = cadViewer;
    this.propertyPanel = null;
    this.contextMenu = null;
    this.tooltips = new Map();

    this.initialize();
  }

  initialize() {
    this.createPropertyPanel();
    this.createContextMenu();
    this.setupTooltips();
    this.addVisualFeedback();

    console.log('[CADUIEnhancements] Initialized');
  }

  /**
   * Create property panel for editing feature parameters
   */
  createPropertyPanel() {
    const panel = document.createElement('div');
    panel.id = 'cadPropertyPanel';
    panel.className = 'cad-property-panel';
    panel.style.cssText = `
      position: absolute;
      right: 20px;
      top: 80px;
      width: 280px;
      background: rgba(26, 26, 26, 0.95);
      backdrop-filter: blur(20px);
      border: 1px solid rgba(191, 90, 242, 0.3);
      border-radius: 12px;
      padding: 16px;
      display: none;
      z-index: 1000;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
    `;

    panel.innerHTML = `
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;">
        <h4 style="margin: 0; color: #BF5AF2; font-size: 14px;">Properties</h4>
        <button id="btnClosePropertyPanel" style="background: none; border: none; color: #fff; cursor: pointer; font-size: 20px; padding: 0; width: 24px; height: 24px;">&times;</button>
      </div>
      <div id="propertyPanelContent" style="color: #fff; font-size: 12px;">
        <p style="color: rgba(255,255,255,0.5);">Select a feature to edit properties</p>
      </div>
    `;

    document.body.appendChild(panel);
    this.propertyPanel = panel;

    // Wire up close button
    panel.querySelector('#btnClosePropertyPanel').onclick = () => {
      panel.style.display = 'none';
    };
  }

  /**
   * Show properties for selected feature
   */
  showFeatureProperties(featureId) {
    const feature = this.cadViewer.document?.getFeature(featureId);
    if (!feature) return;

    const content = this.propertyPanel.querySelector('#propertyPanelContent');

    // Build property UI based on feature type
    let html = `
      <div style="margin-bottom: 12px;">
        <label style="display: block; margin-bottom: 4px; color: rgba(255,255,255,0.7);">Name:</label>
        <input type="text" id="propFeatureName" value="${feature.name || feature.type}"
          style="width: 100%; padding: 6px; background: #2a2a2a; color: #fff; border: 1px solid #444; border-radius: 4px;">
      </div>
    `;

    // Add type-specific properties
    switch (feature.type) {
      case 'extrude':
        html += `
          <div style="margin-bottom: 12px;">
            <label style="display: block; margin-bottom: 4px; color: rgba(255,255,255,0.7);">Distance:</label>
            <input type="number" id="propExtrudeDistance" value="${feature.parameters.distance}" step="0.1"
              style="width: 100%; padding: 6px; background: #2a2a2a; color: #fff; border: 1px solid #444; border-radius: 4px;">
          </div>
          <div style="margin-bottom: 12px;">
            <label style="display: block; margin-bottom: 4px; color: rgba(255,255,255,0.7);">Direction:</label>
            <select id="propExtrudeDirection" style="width: 100%; padding: 6px; background: #2a2a2a; color: #fff; border: 1px solid #444; border-radius: 4px;">
              <option value="normal">Normal</option>
              <option value="reverse">Reverse</option>
            </select>
          </div>
        `;
        break;

      case 'revolve':
        html += `
          <div style="margin-bottom: 12px;">
            <label style="display: block; margin-bottom: 4px; color: rgba(255,255,255,0.7);">Angle (degrees):</label>
            <input type="number" id="propRevolveAngle" value="${(feature.parameters.angle * 180 / Math.PI).toFixed(0)}" step="15" min="0" max="360"
              style="width: 100%; padding: 6px; background: #2a2a2a; color: #fff; border: 1px solid #444; border-radius: 4px;">
          </div>
        `;
        break;

      case 'fillet':
        html += `
          <div style="margin-bottom: 12px;">
            <label style="display: block; margin-bottom: 4px; color: rgba(255,255,255,0.7);">Radius:</label>
            <input type="number" id="propFilletRadius" value="${feature.parameters.radius}" step="0.01" min="0"
              style="width: 100%; padding: 6px; background: #2a2a2a; color: #fff; border: 1px solid #444; border-radius: 4px;">
          </div>
        `;
        break;

      case 'chamfer':
        html += `
          <div style="margin-bottom: 12px;">
            <label style="display: block; margin-bottom: 4px; color: rgba(255,255,255,0.7);">Distance:</label>
            <input type="number" id="propChamferDistance" value="${feature.parameters.distance}" step="0.01" min="0"
              style="width: 100%; padding: 6px; background: #2a2a2a; color: #fff; border: 1px solid #444; border-radius: 4px;">
          </div>
        `;
        break;
    }

    // Add update button
    html += `
      <button id="btnUpdateFeature" style="width: 100%; padding: 10px; margin-top: 16px; background: #BF5AF2; color: #fff; border: none; border-radius: 6px; cursor: pointer; font-weight: 600;">
        Update Feature
      </button>
    `;

    content.innerHTML = html;
    this.propertyPanel.style.display = 'block';

    // Wire up update button
    content.querySelector('#btnUpdateFeature').onclick = () => {
      this.updateFeatureFromProperties(featureId);
    };
  }

  /**
   * Update feature from property panel values
   */
  updateFeatureFromProperties(featureId) {
    const feature = this.cadViewer.document?.getFeature(featureId);
    if (!feature) return;

    // Update name
    const newName = document.getElementById('propFeatureName')?.value;
    if (newName) {
      feature.name = newName;
    }

    // Update type-specific properties
    let needsRecompute = false;

    switch (feature.type) {
      case 'extrude':
        const newDistance = parseFloat(document.getElementById('propExtrudeDistance')?.value);
        if (!isNaN(newDistance) && newDistance !== feature.parameters.distance) {
          feature.parameters.distance = newDistance;
          needsRecompute = true;
        }
        break;

      case 'revolve':
        const newAngle = parseFloat(document.getElementById('propRevolveAngle')?.value);
        if (!isNaN(newAngle)) {
          feature.parameters.angle = (newAngle * Math.PI) / 180;
          needsRecompute = true;
        }
        break;

      case 'fillet':
        const newRadius = parseFloat(document.getElementById('propFilletRadius')?.value);
        if (!isNaN(newRadius)) {
          feature.parameters.radius = newRadius;
          needsRecompute = true;
        }
        break;

      case 'chamfer':
        const newChamfer = parseFloat(document.getElementById('propChamferDistance')?.value);
        if (!isNaN(newChamfer)) {
          feature.parameters.distance = newChamfer;
          needsRecompute = true;
        }
        break;
    }

    if (needsRecompute) {
      // Recompute the feature
      this.cadViewer.recomputeFeature(featureId);
      Utils.notify('Feature updated', 'success');
    }

    this.cadViewer.updateFeatureTree();
    this.cadViewer.document.hasUnsavedChanges = true;
  }

  /**
   * Create context menu for right-click actions
   */
  createContextMenu() {
    const menu = document.createElement('div');
    menu.id = 'cadContextMenu';
    menu.style.cssText = `
      position: absolute;
      background: #1a1a1a;
      border: 1px solid rgba(191, 90, 242, 0.3);
      border-radius: 8px;
      padding: 4px;
      display: none;
      z-index: 10000;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
      min-width: 180px;
    `;

    document.body.appendChild(menu);
    this.contextMenu = menu;

    // Hide on click outside
    document.addEventListener('click', () => {
      menu.style.display = 'none';
    });
  }

  /**
   * Show context menu at position
   */
  showContextMenu(x, y, actions) {
    this.contextMenu.innerHTML = '';

    actions.forEach(action => {
      const item = document.createElement('div');
      item.style.cssText = `
        padding: 8px 12px;
        color: #fff;
        cursor: pointer;
        font-size: 12px;
        border-radius: 4px;
        transition: background 0.2s;
      `;
      item.textContent = action.label;

      item.onmouseover = () => {
        item.style.background = 'rgba(191, 90, 242, 0.2)';
      };
      item.onmouseout = () => {
        item.style.background = 'transparent';
      };

      item.onclick = (e) => {
        e.stopPropagation();
        action.callback();
        this.contextMenu.style.display = 'none';
      };

      this.contextMenu.appendChild(item);
    });

    this.contextMenu.style.left = x + 'px';
    this.contextMenu.style.top = y + 'px';
    this.contextMenu.style.display = 'block';
  }

  /**
   * Setup tooltips for buttons
   */
  setupTooltips() {
    // Tooltips are handled by title attributes
    // Could enhance with custom tooltip component if needed
  }

  /**
   * Add visual feedback for interactions
   */
  addVisualFeedback() {
    // Add hover effects, click feedback, etc.
    // This is handled by CSS mostly
  }

  /**
   * Show DOF (Degrees of Freedom) status for sketches
   */
  showDOFStatus(sketchId) {
    const sketch = this.cadViewer.document?.getFeature(sketchId);
    if (!sketch || sketch.type !== 'sketch') return;

    // Calculate DOF
    const entities = sketch.parameters.entities || [];
    const constraints = sketch.parameters.constraints || [];

    // Simplified DOF calculation
    // Each point has 2 DOF, each constraint removes DOF
    let totalDOF = entities.length * 2;
    let constrainedDOF = constraints.length;

    const freeDOF = totalDOF - constrainedDOF;

    let status, color;
    if (freeDOF === 0) {
      status = 'Fully Constrained';
      color = '#00FF00';
    } else if (freeDOF < 0) {
      status = 'Over-Constrained';
      color = '#FF0000';
    } else {
      status = `Under-Constrained (${freeDOF} DOF)`;
      color = '#FFA500';
    }

    Utils.notify(`${status}`, 'info');

    // Show in feature tree
    const featureItem = document.querySelector(`[data-feature-id="${sketchId}"]`);
    if (featureItem) {
      let badge = featureItem.querySelector('.dof-badge');
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'dof-badge';
        badge.style.cssText = `
          font-size: 9px;
          padding: 2px 6px;
          border-radius: 10px;
          margin-left: 6px;
        `;
        featureItem.querySelector('.feature-name').appendChild(badge);
      }

      badge.textContent = freeDOF === 0 ? '✓' : freeDOF;
      badge.style.background = color + '40';
      badge.style.color = color;
    }
  }

  /**
   * Show measurement overlay in viewport
   */
  showMeasurementOverlay(measurement) {
    const overlay = document.createElement('div');
    overlay.className = 'cad-measurement-overlay';
    overlay.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(191, 90, 242, 0.95);
      color: #fff;
      padding: 12px 20px;
      border-radius: 8px;
      font-size: 16px;
      font-weight: 600;
      z-index: 9999;
      pointer-events: none;
      animation: fadeInOut 2s ease-in-out;
    `;

    overlay.textContent = measurement;

    const viewport = document.getElementById('viewport');
    if (viewport) {
      viewport.appendChild(overlay);

      setTimeout(() => {
        overlay.remove();
      }, 2000);
    }
  }

  /**
   * Highlight feature in tree and 3D view
   */
  highlightFeature(featureId, highlight = true) {
    // Highlight in tree
    const treeItem = document.querySelector(`[data-feature-id="${featureId}"]`);
    if (treeItem) {
      if (highlight) {
        treeItem.style.background = 'rgba(191, 90, 242, 0.2)';
        treeItem.style.borderLeft = '3px solid #BF5AF2';
      } else {
        treeItem.style.background = '';
        treeItem.style.borderLeft = '';
      }
    }

    // Highlight in 3D
    const mesh = this.cadViewer.cadObjects.get(featureId);
    if (mesh && mesh.material) {
      if (highlight) {
        mesh.material.emissive = new THREE.Color(0x4400FF);
        mesh.material.emissiveIntensity = 0.3;
      } else {
        mesh.material.emissive = new THREE.Color(0x000000);
        mesh.material.emissiveIntensity = 0;
      }
    }
  }

  /**
   * Show operation in progress indicator
   */
  showProgressIndicator(message) {
    const indicator = document.createElement('div');
    indicator.id = 'cadProgressIndicator';
    indicator.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(26, 26, 26, 0.95);
      backdrop-filter: blur(20px);
      border: 1px solid rgba(191, 90, 242, 0.5);
      border-radius: 8px;
      padding: 12px 24px;
      color: #BF5AF2;
      font-size: 14px;
      z-index: 10000;
      display: flex;
      align-items: center;
      gap: 12px;
    `;

    indicator.innerHTML = `
      <div class="spinner" style="width: 16px; height: 16px; border: 2px solid rgba(191, 90, 242, 0.3); border-top: 2px solid #BF5AF2; border-radius: 50%; animation: spin 1s linear infinite;"></div>
      <span>${message}</span>
    `;

    document.body.appendChild(indicator);

    return {
      remove: () => indicator.remove(),
      update: (newMessage) => {
        indicator.querySelector('span').textContent = newMessage;
      }
    };
  }

  /**
   * Add CSS animations
   */
  addAnimations() {
    if (!document.getElementById('cadAnimations')) {
      const style = document.createElement('style');
      style.id = 'cadAnimations';
      style.textContent = `
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes fadeInOut {
          0% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
          10% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          90% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          100% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
        }

        .cad-feature-item:hover {
          background: rgba(191, 90, 242, 0.1);
        }

        .cad-tool-btn:hover {
          transform: scale(1.05);
        }
      `;
      document.head.appendChild(style);
    }
  }
}

// Export
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { CADUIEnhancements };
}
